/**
 * The frame loop -- one rAF, with the subsystem order written down as data.
 *
 * STEPS is main.js's frame() cut at its seams and laid out as a list, so
 * the order the subsystems run in is one thing to read and one thing to
 * diff, instead of a page of calls whose order is only visible by reading
 * all of it.  The order IS the contract: sendPose-before-update lives in
 * the net step's own comment, the camera poses before chat projects
 * against it, and the render sees everything already moved.  Each step's
 * body is the original code with its original comments; the optional
 * chains stay because their timing is the design -- a system that is not
 * built yet is skipped this frame, exactly as before.
 */
import * as THREE from "three";
import { DebugOverlay } from "../debug.js";
import { game, takeSeat, updateCamera } from "./context.js";
import { achieveFrame } from "../achievements.js";
import { gamepadStep } from "../input/gamepad.js";
import { ui } from "../ui/index.js";

// ---- loop ------------------------------------------------------------------
game.debug = new DebugOverlay({ renderer: game.renderer, scene: game.scene,
                                camera: game.camera, player: game.player,
                                level: game.level, cats: game.cats,
                                // The overlay's own three pieces, plus #hud --
                                // the one panel it toggles that is not its own,
                                // handed across as an explicit handle.
                                els: { root: ui.debug, body: ui.debugBody,
                                       cross: ui.debugCross, toast: ui.debugToast,
                                       hud: ui.hud } });

const clock = new THREE.Clock();

/**
 * The registered subsystems, in the order the frame runs them.  `f` is the
 * frame's scratch -- the capped dt, and the state string the player step
 * hands to everything after it.
 */
export const STEPS = [

  { name: "inputs", run(g, f) {
    const acts = g.cats[g.active] ? g.cats[g.active].actions : {};
    g.input.stalk = g.raw.stalk && acts.stalk ? 1 : 0;
    g.input.swipe = g.raw.swipe && acts.swipe ? 1 : 0;

    const jumpKb = g.jumpHeld || g.jumpPressed ? 1 : 0;
    if (jumpKb) g.input.jump = 1;
    else if (g.jumpKbLast) g.input.jump = 0;
    g.jumpKbLast = jumpKb;
  } },

  { name: "player", run(g, f) {
    f.state = g.player.update(f.dt, g.input, g.camYaw);
    g.input.sitToggle = 0;
    g.raw.swipe = 0;
    g.jumpPressed = 0;
  } },

  { name: "clips", run(g, f) {
    const cat = g.cats[g.active];
    const rate = f.state === "walk" || f.state === "run" || f.state === "stalk"
      ? cat.rateFor(f.state, g.player.speed) : 1;
    // Fades, by transition.  land keeps its 0.06.  Into pounce, 0.10: the pose
    // only shows after 0.09 s of hold (POSE_DELAY), so by then the player has
    // committed to charging and the crouch can gather in rather than snap.  Out
    // of pounce, 0.04 -- two or three frames -- because leaving the pose IS the
    // launch: the release wants to read as instantaneous, and the default 0.15
    // keeps the cat half-crouched through the first 150 ms of flight.
    // Into cling, 0.08: a claw catch is an arrest, and most catches come out
    // of `fall` -- from `jump` the two share an action and skip fading anyway.
    const fade = f.state === "land" ? 0.06
      : f.state === "pounce" ? 0.10
      : f.state === "cling" ? 0.08
      : cat.current === "pounce" ? 0.04
      : 0.15;
    cat.play(f.state, { rate, fade, charge: g.player.charge });
    cat.root.position.copy(g.player.pos);
    cat.root.rotation.y = g.player.facing;
    cat.update(f.dt);
    // gathering, not charging: charging is true on the press frame of every
    // tap-jump too, and flashing the meter open for one frame on a tap left a
    // ghost fading out of the bottom of the screen after every ordinary jump.
    g.meter?.set(g.player.charge, g.player.gathering);
    // The same two numbers, drawn on the jump button instead, where a phone
    // player's eyes already are.
    g.touch?.setCharge(g.player.charge, g.player.gathering);
  } },

  // The same charge, answered as a place instead of a fraction.  After
  // player.update so the arc is flown from the frame the player is actually
  // standing in, and gated on `gathering` inside, on the one threshold the
  // meter and the crouch already share.  Its own step rather than a line
  // inside clips: it is not a clip, and STEPS is the frame order written
  // down -- a subsystem that runs every frame gets a name in that list.
  { name: "aim", run(g, f) {
    g.pounceArc.update(f.dt, g.player, g.pounceOracle);
  } },

  { name: "panels", run(g, f) {
    // A panel opening mid-gesture would otherwise leave a thumb's worth of
    // input latched underneath it -- the cat walking on behind the cat builder,
    // claws out, for as long as the builder is up.  Edge-triggered, and it also
    // takes the buttons off the screen while a panel owns it.
    const panelled = !!(g.customizer?.isOpen || g.join?.isOpen);
    if (panelled !== g.wasPanelled) {
      g.wasPanelled = panelled;
      if (panelled) g.touch?.releaseAll();
      document.documentElement.classList.toggle("panelled", panelled);
    }
  } },

  { name: "net", run(g, f) {
    // The street.  sendPose first so the frame the server sees is the frame we
    // just simulated, then update() advances the render-past clock and poses
    // every other cat against it -- in that order, or the roster is drawn one
    // frame behind the snapshot it already has.
    if (g.net) {
      g.net.sendPose(g.player, f.state);
      g.net.update(f.dt);
      if (g.seatWanted) takeSeat();
      // Our own happiness rides in our own snapshot row like everyone else's:
      // the server owns it, so reading it back off the wire is the only honest
      // source -- and it means the meter and the purr can never disagree with
      // what the other fifteen people see over this cat's head.
      const mine = g.net.slot >= 0 ? g.net.client.sampleLive(g.net.slot) : null;
      if (mine) g.myHappy = mine.happy;
      g.happyHud?.setHappiness(g.myHappy);
      g.purr?.setFromHappiness(g.myHappy);
      achieveFrame(g.myHappy, g.player);   // §4.4's frame edges (FIRST_PURR, NIGHT_DRIVER); the module owns the conditions
    }
  } },

  { name: "butterflies", run(g, f) {
    // The butterflies are told the cat's state, not just its position: scatter
    // is a reaction to being pounced at, and "pounce" is a state, not a place.
    g.butterflies?.update(f.dt, g.player.pos, f.state);
  } },

  { name: "prey", run(g, f) {
    // The prey are told nothing: they are drawn from server rows (the hunt is
    // authoritative), and this call only advances the smoothing and the wings.
    g.prey?.update(f.dt);
  } },

  { name: "dog", run(g, f) {
    g.dog?.update(f.dt);   // likewise: server rows in, smoothing and wags out
  } },

  { name: "level", run(g, f) {
    g.level.update(f.dt);   // ripples included: greenspace registers tickWater here
  } },

  { name: "daycycle", run(g, f) {
    g.daycycle.update(f.dt);   // the sky's clock, before follow() places the key
  } },

  { name: "camera", run(g, f) {
    g.door?.update();
    updateCamera(f.dt);
  } },

  { name: "chat", run(g, f) {
    g.chat.update(f.dt);   // quick-chat bubbles, against the camera just posed
  } },

  { name: "render", run(g, f) {
    g.renderer.render(g.scene, g.camera);
  } },

  { name: "debug", run(g, f) {
    g.debug.update();
  } },

  gamepadStep,   // appended, per the list's append-only design (WS-16): the pad
                 // polls at the frame's tail and is consumed next frame --
                 // input/gamepad.js's header owns the latency arithmetic
];

function frame() {
  requestAnimationFrame(frame);
  const dt = Math.min(clock.getDelta(), 0.05);
  if (!game.ready) return;
  const f = { dt, state: "" };
  for (const step of STEPS) step.run(game, f);
}

function resize() {
  const w = innerWidth, h = innerHeight;
  game.renderer.setSize(w, h, false);
  game.camera.aspect = w / h;
  game.camera.updateProjectionMatrix();
}

/** Start rendering.  The loop runs from the first call -- frames before the
    boot flips game.ready only sample the clock and wait, exactly as before. */
export function start() {
  addEventListener("resize", resize);
  resize();
  frame();
}
